import {ErrorModel} from "./api/error/error-model";
import {ErrorCode} from "./api/error/error-code";

const JWT_SEGMENT_PATTERN = /^[A-Za-z0-9_-]+$/;
const LICENSE_SUBJECT = 'CaptchaBoxLicense';

export class License {
  public customizabilityOption: boolean;
  public invisibilityOption: boolean;
  public whiteLabelOption: boolean;

  private static publicKeyPem: string = `-----BEGIN PUBLIC KEY-----
MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE7p2F00dj2AawXZNv44YEpFmmONYg
KvumZzJIONO4OcqvLe0j3yE9WYMAHNJSli8kUlxalIfexjXK7JkqGu0lsQ==
-----END PUBLIC KEY-----`;

  private constructor(customizabilityOption: boolean, invisibilityOption: boolean, whiteLabelOption: boolean) {
    this.customizabilityOption = customizabilityOption;
    this.invisibilityOption = invisibilityOption;
    this.whiteLabelOption = whiteLabelOption;
  }

  public static async create(token: string, siteKey: string, failureCallback: (error: ErrorModel) => void): Promise<License | undefined> {

    if (!License.hasValidJwtShape(token)) {
      failureCallback(new ErrorModel(ErrorCode.LICENSE_FORMAT_INVALID, "The provided license key has an invalid format."));
      return undefined;
    }

    const decoded = await License.verifyAndDecodeToken(token);
    if (!decoded || decoded.sub !== LICENSE_SUBJECT) {
      failureCallback(new ErrorModel(ErrorCode.LICENSE_INVALID, "The provided license key could not be verified."));
      return undefined;
    }
    if (decoded.siteKey !== siteKey) {
      failureCallback(new ErrorModel(ErrorCode.LICENSE_SITE_KEY_MISMATCH, "The provided license key does not match the configured site key."));
      return undefined;
    }

    const customizability = decoded.features?.customizability ?? false;
    const invisibility = decoded.features?.invisibility ?? false;
    const whiteLabel = decoded.features?.whiteLabel ?? false;
    return new License(customizability, invisibility, whiteLabel);
  }

  private static hasValidJwtShape(token: string): boolean {
    if (typeof token !== 'string' || token.length === 0) return false;
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    return parts.every(part => part.length > 0 && JWT_SEGMENT_PATTERN.test(part));
  }

  private static async verifyAndDecodeToken(token: string): Promise<any> {
    try {
      const [headerB64, payloadB64, signatureB64] = token.split('.');

      const header = JSON.parse(License.base64UrlDecodeToString(headerB64));
      if (header.alg !== 'ES256') return undefined;

      const key = await License.importPublicKey(License.publicKeyPem);
      const signature = License.base64UrlDecodeToBytes(signatureB64);
      const signedData = new TextEncoder().encode(`${headerB64}.${payloadB64}`);

      const verified = await crypto.subtle.verify(
        {name: 'ECDSA', hash: 'SHA-256'},
        key,
        signature as BufferSource,
        signedData,
      );
      if (!verified) return undefined;

      return JSON.parse(License.base64UrlDecodeToString(payloadB64));
    } catch {
      return undefined;
    }
  }

  private static async importPublicKey(pem: string): Promise<CryptoKey> {
    const base64 = pem
      .replace('-----BEGIN PUBLIC KEY-----', '')
      .replace('-----END PUBLIC KEY-----', '')
      .replace(/\s+/g, '');
    const der = License.base64ToBytes(base64);
    return await crypto.subtle.importKey(
      'spki',
      der as BufferSource,
      {name: 'ECDSA', namedCurve: 'P-256'},
      false,
      ['verify'],
    );
  }

  private static base64UrlDecodeToString(input: string): string {
    return new TextDecoder().decode(License.base64UrlDecodeToBytes(input));
  }

  private static base64UrlDecodeToBytes(input: string): Uint8Array {
    let normalized = input.replace(/-/g, '+').replace(/_/g, '/');
    while (normalized.length % 4 !== 0) {
      normalized += '=';
    }
    return License.base64ToBytes(normalized);
  }

  private static base64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}
